import { usePostHog } from 'src/composables/app/ui/usePostHog'
import { log } from 'src/services/app/log.service'
import { refactorMessages } from 'src/services/helpers/message.helper'
import {
  FCM_UPDATE_TYPES,
  FCM_ACTIONS,
  FCM_ERROR_TYPES,
  FCM_BANNER_STATES,
  FCM_BANNER_TITLES,
  FCM_BANNER_MESSAGES,
} from 'src/services/consts/fcm.const'
import { ERROR_TYPES } from 'src/services/consts/api.const'

const { captureException } = usePostHog()

/**
 * Create a pending update ID for FCM badge/count tracking.
 * @param {string} prefix - Use FCM_PENDING_PREFIXES
 * @param {string} entityId - The ticketId or approvalId
 * @returns {string} - e.g. 'ticket-123' or 'approval-456'
 */
export function createPendingId(prefix, entityId) {
  if (!entityId) return ''
  if (!prefix) throw new Error('Prefix is required')
  return `${prefix}-${entityId}`
}

/**
 * Check if notifications are supported in the browser
 * @returns {boolean}
 */
export function isNotificationSupported() {
  return 'Notification' in window
}

/**
 * Get current notification permission status
 * @returns {string} - 'default' | 'granted' | 'denied' | 'unsupported'
 */
export function getNotificationPermission() {
  if (!isNotificationSupported()) {
    return 'unsupported'
  }
  return Notification.permission
}

/**
 * Mask token for display (show first/last 10 chars)
 * @param {string} token
 * @returns {string}
 */
export function maskToken(token) {
  if (!token) return ''
  if (token.length <= 20) return token
  return `${token.substring(0, 10)}...${token.substring(token.length - 10)}`
}

/**
 * Get color for permission status chip
 * @param {string} permission
 * @returns {string}
 */
export function getPermissionColor(permission) {
  // `chrome` — muted neutral fill that stays dark enough for white chip text in both themes
  // (Quasar `grey` does not follow our `--color-*` bridge)
  const colors = {
    default: 'chrome',
    granted: 'positive',
    denied: 'negative',
    unsupported: 'negative',
  }
  return colors[permission] || 'chrome'
}

/**
 * Get color for token status chip
 * @param {string} tokenStatus
 * @returns {string}
 */
export function getTokenStatusColor(tokenStatus) {
  const colors = {
    none: 'chrome',
    fetched: 'warning',
    'sent-to-backend': 'positive',
    error: 'negative',
  }
  return colors[tokenStatus] || 'chrome'
}

/**
 * Format FCM update for display in log
 * @param {object} payload - FCM message payload
 * @returns {object}
 */
export function formatFcmUpdate(payload) {
  // Parse data.payload if it's a JSON string
  if (payload.data?.payload && typeof payload.data.payload === 'string') {
    try {
      payload.data.payload = JSON.parse(payload.data.payload)
    } catch (err) {
      captureException(err, {
        error_type: ERROR_TYPES.FCM,
        error_name: `[${FCM_ERROR_TYPES.PARSE_ERROR}] Failed to parse FCM payload - ${err.message}`,
        original_error_message: err.message,
        error_context: 'FCM: formatFcmUpdate',
      })
      log.error('[FCM] Failed to parse data.payload:', err, payload)
    }
  }

  const parsedData = payload.data.payload || {}

  // Base response object
  const res = {
    timestamp: new Date().toLocaleString(),
    from: payload.from,
    updateType: parsedData.message_type,
    action: parsedData.action,
    ticketId: parsedData.ticket_id,
    approvalId: parsedData.user_auth_id,
    app: parsedData.app,
  }

  // Handle different update types
  switch (res.updateType) {
    case FCM_UPDATE_TYPES.MESSAGES_USER:
      if (res.action === FCM_ACTIONS.NEW) {
        // Transform message object using refactorMessages helper
        const messages = refactorMessages(parsedData.message)
        res.message = messages[0] || null
      } else if (res.action === FCM_ACTIONS.DELETE) {
        res.messageId = parsedData?.message_id
      }
      break
    case FCM_UPDATE_TYPES.TICKET:
      // For ticket updates, we just need the ticket ID which is already in base response
      break
    case FCM_UPDATE_TYPES.USER_AUTHS:
      // Ticket ID and approval ID already in base response
      break
    case FCM_UPDATE_TYPES.DELETE_TICKET:
      // Ticket ID already in base response
      break
    case FCM_UPDATE_TYPES.DELETE_AUTH:
      // Approval ID and ticket ID already in base response
      break
    case FCM_UPDATE_TYPES.DELETE_BOTH:
      // Ticket ID and approval ID already in base response
      break
    default:
      log.error('[FCM] Unknown update type in formatFcmUpdate:', parsedData?.message_type)
  }

  return res
}

/**
 * Check if running on Windows
 * @returns {boolean}
 */
export function isWindows() {
  if (navigator.userAgentData) {
    return navigator.userAgentData.platform === 'Windows'
  }
  return navigator.platform.includes('Win')
}

/**
 * Determine FCM banner state based on status
 * @param {object} status - FCM status object
 * @returns {string} - Banner state constant
 */
export function getFcmBannerState(status) {
  if (!status.isSupported) {
    return FCM_BANNER_STATES.NOT_SUPPORTED
  }

  if (status.permission === 'denied') {
    return FCM_BANNER_STATES.BROWSER_BLOCKED
  }

  if (status.tokenStatus === 'error') {
    const isFirebaseError = status.lastError?.name === 'FirebaseError'
    if (isFirebaseError) {
      return FCM_BANNER_STATES.INIT_ERROR
    }
    // On Windows with granted permission, likely OS-level block
    if (status.permission === 'granted' && isWindows()) {
      return FCM_BANNER_STATES.OS_BLOCKED
    }
    return FCM_BANNER_STATES.INIT_ERROR
  }

  return FCM_BANNER_STATES.UNAVAILABLE
}

/**
 * Get banner content (title and message)
 * @param {object} status - FCM status object
 * @returns {object} - { title, message }
 */
export function getFcmBannerContent(status) {
  const state = getFcmBannerState(status)
  return {
    title: FCM_BANNER_TITLES[state],
    message: FCM_BANNER_MESSAGES[state],
  }
}

/**
 * Check if FCM banner should be shown
 * @param {object} status - FCM status object
 * @param {boolean} isDismissed - Whether user dismissed the banner
 * @returns {boolean}
 */
export function shouldShowFcmBanner(status, isDismissed) {
  if (!status.firstInit) return false
  if (isDismissed) return false
  if (status.isProcessing) return false
  if (status.tokenStatus === 'sent-to-backend') return false

  const state = getFcmBannerState(status)
  return state !== FCM_BANNER_STATES.UNAVAILABLE
}
